/**
 * 「猫が集まる」条件の判定（§3.6）。
 *
 * ここで絶対にやらないこと：**合成した「快適度」を作ること**（§2.1 / §10.1）。
 * 判定は独立した条件の AND で、満たせていない条件は必ず
 * 「どこが」とセットで返す。数値ひとつに丸めた瞬間に、
 * プレイヤーに見せられる情報が「72」みたいなものになってしまう。
 */
import { BED, keyOf } from './grid';
import { NodeKey, bfs } from './graph';
import { RngState, rngFloat } from './rng';
import { GameState } from './state';
import { World, graphFor } from './world';

export type ComfortIssue = 'no-bed' | 'no-water' | 'no-food' | 'crowded';

export interface ComfortIssueAt {
  why: ComfortIssue;
  /** その条件が引っかかっている場所。無ければ null */
  at: NodeKey | null;
}

export interface Comfort {
  ok: boolean;
  issues: ComfortIssueAt[];
  /** 空いている寝床 */
  freeBeds: NodeKey[];
  /** 埋まっている寝床 */
  takenBeds: NodeKey[];
}

/** 同時にこれ以上の猫が「前がつかえて待って」いたら、混んでいる */
export const CROWD_LIMIT = 2;
/** 条件が続けて満たされている必要のある秒数。満たされ続けた時だけ猫が来る */
export const SETTLE_SEC = 25;
/** 快適条件を確かめる間隔（シム内秒）。毎tickやらない（§5.2） */
export const COMFORT_INTERVAL = 0.5;
/**
 * 次に来る猫の種類を決める（§3.3）。
 * 重みつきの抽選。決定論を守るため rng は状態に載っているものを使う。
 */
export function pickArrivalSpecies(world: World, rng: RngState): string {
  const list = [...world.species.values()];
  const total = list.reduce((a, s) => a + Math.max(0, s.arrivalWeight ?? 0), 0);
  if (total <= 0) return list[0]?.id ?? 'stray';
  let roll = rngFloat(rng) * total;
  for (const s of list) {
    roll -= Math.max(0, s.arrivalWeight ?? 0);
    if (roll <= 0) return s.id;
  }
  return list[list.length - 1].id;
}

export function bedsIn(state: GameState): NodeKey[] {
  const out: NodeKey[] = [];
  for (let i = 0; i < state.grid.furniture.length; i++) {
    if (state.grid.furniture[i] === BED) out.push(i);
  }
  return out;
}

export function entranceNode(state: GameState): NodeKey {
  return keyOf(state.grid, state.entrance.c, state.entrance.r);
}

/**
 * 新しく来る猫の目線で、この建物が住めるかを見る。
 * 出発点は割れた窓（外から入ってくる場所）。
 */
export function checkComfort(world: World, speciesId = 'stray'): Comfort {
  const state = world.state;
  const graph = graphFor(world, speciesId);
  const reach = bfs(graph, [entranceNode(state)]);

  const freeBeds: NodeKey[] = [];
  const takenBeds: NodeKey[] = [];
  for (const bed of bedsIn(state)) {
    if (!reach.has(bed)) continue; // 行けない寝床は無いのと同じ
    if (state.cats.some((c) => c.node === bed)) takenBeds.push(bed);
    else freeBeds.push(bed);
  }

  const issues: ComfortIssueAt[] = [];

  // 1. 空いている寝床があるか
  if (freeBeds.length === 0) {
    issues.push({ why: 'no-bed', at: takenBeds.length ? takenBeds[0] : null });
  }

  // 2. 水場へ行けるか
  const water = nearestSpot(state, reach, state.water);
  if (water === null) issues.push({ why: 'no-water', at: keyOf(state.grid, state.water.c, state.water.r) });

  // 3. 餌場へ行けるか
  const bowl = nearestSpot(state, reach, state.bowl);
  if (bowl === null) issues.push({ why: 'no-food', at: keyOf(state.grid, state.bowl.c, state.bowl.r) });

  // 4. 主要経路が混んでいないか
  const waiting = state.cats.filter((c) => c.waiting > 0);
  if (waiting.length > CROWD_LIMIT) {
    issues.push({ why: 'crowded', at: waiting[0].node });
  }

  return { ok: issues.length === 0, issues, freeBeds, takenBeds };
}

function nearestSpot(
  state: GameState,
  reach: Map<NodeKey, NodeKey | null>,
  spot: { c: number; r: number },
): NodeKey | null {
  for (let dc = -1; dc <= 1; dc++) {
    const k = keyOf(state.grid, spot.c + dc, spot.r);
    if (reach.has(k)) return k;
  }
  return null;
}
